import React, { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, ArrowLeft, ArrowRight, ZoomIn, ZoomOut } from 'lucide-react';
import { useZoomPan } from '../../utils/hooks/useZoomPan';
import { useLocalization } from '../../utils/hooks/useLocalization';
import styles from './ImageLightbox.module.css';

/**
 * Fullscreen image viewer with zoom.
 *
 * Desktop: wheel zooms at the cursor, click toggles zoom, drag pans when zoomed,
 * arrows navigate, Esc closes. Mobile: pinch and double-tap zoom, drag pans when
 * zoomed, horizontal swipe navigates and swipe-down dismisses (both only at 1x).
 *
 * Portalled to <body>: .app sets overflow-x: hidden and the Navbar/FAB sit at
 * z-index 1000, so rendering in place would mean fighting a stacking context.
 */
const ImageLightbox = ({ images, index, name, onIndexChange, onClose }) => {
  const { t } = useLocalization();
  const count = images.length;
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
  const closeRef = useRef(null);

  const navRef = useRef(null);
  const navigate = useCallback((dir) => navRef.current(dir), []);

  const { ready, isZoomed, overlayRef, stageProps, imgProps, reset, toggleZoom } = useZoomPan({
    isMobile,
    onNavigate: navigate,
    onDismiss: onClose,
    onBackdropTap: onClose,
  });

  navRef.current = (dir) => {
    if (count < 2) return;
    reset(false, true);                       // drop zoom and gesture state first
    onIndexChange((index + dir + count) % count);
  };

  // The position:fixed lock is the only one iOS honours.
  useEffect(() => {
    const y = window.scrollY;
    const b = document.body.style;
    const prev = { position: b.position, top: b.top, left: b.left, right: b.right, width: b.width, overflow: b.overflow };
    Object.assign(b, { position: 'fixed', top: `-${y}px`, left: '0', right: '0', width: '100%', overflow: 'hidden' });
    return () => { Object.assign(b, prev); window.scrollTo(0, y); };
  }, []);

  // Focus into the dialog on open, back to the trigger on close.
  useEffect(() => {
    const trigger = document.activeElement;
    if (closeRef.current) closeRef.current.focus();
    return () => { if (trigger && trigger.focus) trigger.focus(); };
  }, []);

  // Bound to window because focus may sit on the overlay itself.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') navigate(-1);
      else if (e.key === 'ArrowRight') navigate(1);
      else if (e.key === 'Tab') {
        const btns = overlayRef.current ? [...overlayRef.current.querySelectorAll('button')] : [];
        if (!btns.length) return;
        const edge = e.shiftKey ? btns[0] : btns[btns.length - 1];
        // Clicking the stage drops focus to <body>, so anything outside counts as the edge.
        if (document.activeElement === edge || !overlayRef.current.contains(document.activeElement)) {
          e.preventDefault();
          (e.shiftKey ? btns[btns.length - 1] : btns[0]).focus();
        }
        return;
      } else return;
      e.preventDefault();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [navigate, onClose, overlayRef]);

  // Preload neighbours so a committed swipe never lands on a blank frame.
  useEffect(() => {
    if (count < 2) return;
    [(index + 1) % count, (index - 1 + count) % count].forEach((i) => { new Image().src = images[i]; });
  }, [index, images, count]);

  const stop = (e) => e.stopPropagation();

  return createPortal(
    <div ref={overlayRef} className={styles.overlay} role="dialog" aria-modal="true" aria-label={name} tabIndex={-1}>
      {/* touch-action is the only thing that stops the browser claiming the
          gesture; preventDefault on pointermove does not. */}
      <div {...stageProps} className={styles.stage} style={{ touchAction: 'none' }}>
        <img
          {...imgProps}
          key={index}
          src={images[index]}
          alt={`${name} ${index + 1}`}
          className={styles.image}
          decoding="async"
        />
        {!ready && <span className={styles.spinner} aria-hidden="true" />}
      </div>

      <div className={styles.topBar}>
        {count > 1 && <span className={styles.counter}>{index + 1} / {count}</span>}
        <div className={styles.topActions}>
          <button
            type="button"
            className={styles.iconBtn}
            onPointerDown={stop}
            onClick={() => toggleZoom(window.innerWidth / 2, window.innerHeight / 2)}
            aria-label={t(isZoomed ? 'product_detail_gallery_zoom_out' : 'product_detail_gallery_zoom')}
          >
            {isZoomed ? <ZoomOut size={22} /> : <ZoomIn size={22} />}
          </button>
          <button
            type="button"
            ref={closeRef}
            className={styles.iconBtn}
            onPointerDown={stop}
            onClick={onClose}
            aria-label={t('product_detail_gallery_close')}
          >
            <X size={22} />
          </button>
        </div>
      </div>

      {count > 1 && [
        { dir: -1, cls: styles.navPrev, Icon: ArrowLeft, key: 'product_detail_gallery_prev' },
        { dir: 1, cls: styles.navNext, Icon: ArrowRight, key: 'product_detail_gallery_next' },
      ].map(({ dir, cls, Icon, key }) => (
        <button
          key={key}
          type="button"
          className={`${styles.navBtn} ${cls}`}
          onPointerDown={stop}
          onClick={() => navigate(dir)}
          aria-label={t(key)}
        >
          <Icon size={26} />
        </button>
      ))}
    </div>,
    document.body
  );
};

export default ImageLightbox;
